import {Injectable} from '@angular/core';

import {Observable, ReplaySubject} from 'rxjs';

import {Country} from './country';
import {EmailState} from './email-state';
import {FinancingMethod} from './financing-method';
import {InvoiceAttachmentType} from './invoice-attachment-type';
import {InvoiceConfiguration} from './invoice-configuration';
import {InvoiceState} from './invoice-state';
import {Model, ModelKey, ModelKeyOf} from './model';
import {
  CountriesResource, EmailStatesResource,
  FinancingMethodsResource,
  InvoiceAttachmentTypesResource,
  InvoiceConfigurationsResource,
  InvoiceStatesResource,
  ModelResource,
  PaymentTransferStatusesResource,
  PenaltyInterestMethodsResource,
  UsersResource
} from './model-resource';
import {ObjectQueryParameters} from './object-query-parameters';
import {PaymentTransferStatus} from './payment-transfer-status';
import {PenaltyInterestMethod} from './penalty-interest-method';
import {User} from './user';

export class ModelMap<T extends Model<K>, K extends ModelKey = ModelKeyOf<T>> {

  private readonly map = new Map<K, T>();

  add(ts: T[]): void {
    ts.forEach((t: T) => this.map.set(t.getPrimaryKey(), t));
  }

  public get(key: K): T | null {
    return this.map.get(key);
  }

  all(): T[] {
    return Array.from(this.map.values());
  }
}

export class ModelMapCache<T extends Model<K>, K extends ModelKey = ModelKeyOf<T>> {

  public map$: Observable<ModelMap<T, K>>;

  private subject: ReplaySubject<ModelMap<T, K>>;
  private resource: ModelResource<T, K>;

  constructor(resource: ModelResource<T, K>) {
    this.resource = resource;
    this.subject = new ReplaySubject<ModelMap<T, K>>(1);
    this.map$ = this.subject.asObservable();
    this.refresh();
  }

  private refresh(): void {
    const params = new ObjectQueryParameters();
    params.setPager(1, 999); // TODO: Fix with more elegant solution
    const subscription = this.resource
      .query(params)
      .subscribe((ts: T[]) => {
        const map = new ModelMap<T, K>();
        map.add(ts);
        this.subject.next(map);
        subscription.unsubscribe();
      });
  }
}

@Injectable()
export class CountriesMap extends ModelMap<Country> {
}

@Injectable()
 export class EmailStatesMap extends ModelMap<EmailState> {
}

@Injectable()
export class FinancingMethodsMap extends ModelMap<FinancingMethod> {
}

@Injectable()
export class InvoiceConfigurationsMap extends ModelMap<InvoiceConfiguration> {
}

@Injectable()
export class InvoiceAttachmentTypesMap extends ModelMap<InvoiceAttachmentType> {
}

@Injectable()
export class InvoiceStatesMap extends ModelMap<InvoiceState> {
}

@Injectable()
export class PenaltyInterestMethodsMap extends ModelMap<PenaltyInterestMethod> {
}

@Injectable()
export class UsersMap extends ModelMap<User> {
}


@Injectable()
export class CountriesMapCache extends ModelMapCache<Country> {
  constructor(resource: CountriesResource) {
    super(resource);
  }
}

@Injectable()
export class EmailStatesMapCache extends ModelMapCache<EmailState> {
  constructor(resource: EmailStatesResource) {
    super(resource);
  }
}

@Injectable()
export class FinancingMethodsMapCache extends ModelMapCache<FinancingMethod> {
  constructor(resource: FinancingMethodsResource) {
    super(resource);
  }
}

@Injectable()
export class InvoiceConfigurationsMapCache extends ModelMapCache<InvoiceConfiguration> {
  constructor(resource: InvoiceConfigurationsResource) {
    super(resource);
  }
}

@Injectable()
export class InvoiceAttachmentTypesMapCache extends ModelMapCache<InvoiceAttachmentType> {
  constructor(resource: InvoiceAttachmentTypesResource) {
    super(resource);
  }
}

@Injectable()
export class InvoiceStatesMapCache extends ModelMapCache<InvoiceState> {
  constructor(resource: InvoiceStatesResource) {
    super(resource);
  }
}

@Injectable()
export class PaymentTransferStatusesMapCache extends ModelMapCache<PaymentTransferStatus> {
  constructor(resource: PaymentTransferStatusesResource) {
    super(resource);
  }
}

@Injectable()
export class PenaltyInterestMethodsMapCache extends ModelMapCache<PenaltyInterestMethod> {
  constructor(resource: PenaltyInterestMethodsResource) {
    super(resource);
  }
}

@Injectable()
export class UsersMapCache extends ModelMapCache<User> {
  constructor(resource: UsersResource) {
    super(resource);
  }
}
